// Client-side prediction for wall place / break actions.
//
// Goal: brief §3 + Phase 1 done-criterion ("Wall placement feels
// instant — no perceived input delay"). When the user clicks LMB in
// build mode, the wall must appear THIS frame, not 50-150 ms later
// when the next snapshot lands.
//
// How it works (mirrors `PredictionEngine` for movement):
//
//   1. Click LMB → `tryPlace()` evaluates the same gates the server
//      would (mana, build radius, empty cell, no-trap) and, if all
//      pass, appends a `PendingAction` to a list.
//   2. The renderer pulls `predictedAdditions()` and `predictedRemovals()`
//      every frame, unions them with the server snapshot's wall list,
//      and draws the merged result. Walls in flight look identical to
//      confirmed walls — no flicker, no "ghost" visuals.
//   3. The server eventually processes the input. Its next snapshot
//      either contains the wall (we predicted right) or doesn't (the
//      server rejected, e.g. you ran out of mana between client check
//      and server tick). `reconcile()` drops every pending action with
//      `tick <= lastInputTick` — at that point the snapshot's wall set
//      is authoritative and the pending overlay is no longer needed.
//
// Ownership tracking: snapshots don't carry per-wall owner_id, so to
// predict BREAKs accurately we remember which cells the client placed.
// `ownedCells` grows when the server confirms one of our pending places
// and shrinks when one of our walls vanishes from a snapshot (broken
// by us or by an enemy shot). `tryBreak` only schedules cells we're
// confident are ours, avoiding the enemy-wall flicker that an ownership-
// blind predictor would produce.
//
// EVERY constant + every guard rule MUST stay identical to
// `server::game::systems::{try_place, sweep_break}`. Anywhere this
// diverges, the user sees a wall appear locally and then pop away ~100 ms
// later when the server's reality lands. The HUD's `pendBuild:` line
// surfaces the in-flight count so unexpected divergences are visible.

/** Pack a signed 32-bit cell coord pair into one 32-bit number for Set
 *  membership. Identical encoding to `viewport.ts::packCellKey` so the
 *  two layers can share keys without translation. */
export function packCellKey(x: number, y: number): number {
  return ((x & 0xffff) << 16) | (y & 0xffff);
}

// ---------------------------------------------------------------------------
// Tunables — MUST MATCH server::game::systems exactly.
// ---------------------------------------------------------------------------

/** Center-to-player distance allowed for place + break. */
const BUILD_RADIUS = 5;
const BUILD_RADIUS_SQ = BUILD_RADIUS * BUILD_RADIUS;

/** Player AABB half-extent used by the no-trap check. Matches
 *  `PLAYER_HALF.x` on the server (see `server::game::systems`). */
const PLAYER_HALF = 0.7;
const PLAYER_HALF_SQ = PLAYER_HALF * PLAYER_HALF;

// Break-AoE radius lives in `input.ts` now — the input pump is what
// expands the cursor into a cell batch each frame. `BuildPrediction`
// just validates per-cell.

export interface Vec2 {
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Pending action queue
// ---------------------------------------------------------------------------

interface PendingPlace {
  type: "place";
  tick: number;
  cellX: number;
  cellY: number;
}

interface PendingBreak {
  type: "break";
  tick: number;
  cells: Array<{ x: number; y: number }>;
}

type PendingAction = PendingPlace | PendingBreak;

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

export class BuildPrediction {
  /** Chronological queue of in-flight actions. Entries are dropped by
   *  `reconcile` once the server's `lastInputTick` has caught up. */
  private pending: PendingAction[] = [];

  /** Cells the latest snapshot confirms we own. Used by `tryBreak` to
   *  decide which AoE cells to optimistically remove — predicting
   *  enemy walls would cause visible pop-back when the server rejects
   *  the break. Maintained by `reconcile`; never written elsewhere. */
  private readonly ownedCells = new Set<number>();

  /** Try to predict a wall placement at an EXPLICIT cell coord. The
   *  input pump picks cells (at native mouse rate, batched per 30 Hz
   *  tick); this just validates one and queues it. Mirrors
   *  `server::try_place_cell`'s rules.
   *
   *  Returns true if accepted, false if any of the server's rejection
   *  rules would also reject it. The caller can ignore the return —
   *  rejection is silent; the renderer just won't paint the cell. */
  tryPlaceCell(
    tick: number,
    cellX: number,
    cellY: number,
    playerPos: Vec2,
    availableMana: number,
    serverWalls: Set<number>,
  ): boolean {
    // Same gate cascade as `server::try_place_cell`:
    //   (a) mana available
    //   (b) target cell center within BUILD_RADIUS of player
    //   (c) target cell currently empty (no server wall AND no pending place)
    //   (d) placement wouldn't trap the player
    if (availableMana < 1) return false;

    const centerX = cellX + 0.5;
    const centerY = cellY + 0.5;

    // Build-radius gate.
    const dxp = centerX - playerPos.x;
    const dyp = centerY - playerPos.y;
    if (dxp * dxp + dyp * dyp > BUILD_RADIUS_SQ) return false;

    // Empty-cell gate against the merged display set: server walls +
    // pending places - pending breaks. Holding LMB on the same cell
    // returns false on subsequent ticks (cell now pending-placed); a
    // break-then-replace on the same cell within RTT correctly allows
    // the second place (the prior place is cancelled by the break in
    // the merged set).
    const key = packCellKey(cellX, cellY);
    if (this.displayedWalls(serverWalls).has(key)) return false;

    // Trap gate: closest point on the cell AABB to the player center
    // must be outside PLAYER_HALF. Same math as
    // `server::aabb_overlaps_player_circle`.
    const xMin = cellX;
    const yMin = cellY;
    const xMax = cellX + 1;
    const yMax = cellY + 1;
    const cx = Math.max(xMin, Math.min(xMax, playerPos.x));
    const cy = Math.max(yMin, Math.min(yMax, playerPos.y));
    const dx = playerPos.x - cx;
    const dy = playerPos.y - cy;
    if (dx * dx + dy * dy < PLAYER_HALF_SQ) return false;

    this.pending.push({ type: "place", tick, cellX, cellY });
    return true;
  }

  /** Try to predict a wall BREAK at an explicit cell coord. The AoE
   *  expansion now happens in the InputPump (it seeds the break batch
   *  with cells within `BREAK_AOE_RADIUS` of the cursor on each
   *  mousemove); this method just validates ONE cell and queues it if
   *  it's currently a wall AND ours.
   *
   *  Returns true if accepted. Filters non-own cells silently — that's
   *  what avoids enemy-wall flicker when an RMB sweep brushes over a
   *  mix of own + enemy cells. */
  tryBreakCell(
    tick: number,
    cellX: number,
    cellY: number,
    playerPos: Vec2,
    serverWalls: Set<number>,
  ): boolean {
    const centerX = cellX + 0.5;
    const centerY = cellY + 0.5;

    // Build-radius gate: server still applies it on break.
    const dxp = centerX - playerPos.x;
    const dyp = centerY - playerPos.y;
    if (dxp * dxp + dyp * dyp > BUILD_RADIUS_SQ) return false;

    const key = packCellKey(cellX, cellY);

    // Must be a wall currently — server walls + pending places, minus
    // pending breaks (so a cell we already pending-broke this tick
    // doesn't double-count for mana refund).
    if (!this.displayedWalls(serverWalls).has(key)) return false;

    // Must be ours. Cells in `ownedCells` are server-confirmed; cells
    // in a pending PLACE we've issued count too (user may break a
    // wall they just placed before the server ever sees it).
    if (!this.ownedCells.has(key) && !this.pendingPlaceContains(key)) {
      return false;
    }

    this.pending.push({ type: "break", tick, cells: [{ x: cellX, y: cellY }] });
    return true;
  }

  /** True if some pending PLACE targets this cell. */
  private pendingPlaceContains(cellKey: number): boolean {
    for (const a of this.pending) {
      if (a.type === "place" && packCellKey(a.cellX, a.cellY) === cellKey) {
        return true;
      }
    }
    return false;
  }

  /** Net mana delta from pending actions. Negative = spent, positive
   *  = refunded. Add to the snapshot's `your_mana` to get the displayed
   *  value. */
  predictedManaDelta(): number {
    let delta = 0;
    for (const a of this.pending) {
      if (a.type === "place") {
        delta -= 1;
      } else {
        delta += a.cells.length;
      }
    }
    return delta;
  }

  /** Compute the set of cells that should render as walls right now:
   *  server's walls plus our predicted placements, minus our predicted
   *  removals. Applied in tick order so place-then-break-on-same-cell
   *  yields the post-break state. */
  displayedWalls(serverWalls: Set<number>): Set<number> {
    const out = new Set(serverWalls);
    for (const a of this.pending) {
      if (a.type === "place") {
        out.add(packCellKey(a.cellX, a.cellY));
      } else {
        for (const c of a.cells) {
          out.delete(packCellKey(c.x, c.y));
        }
      }
    }
    return out;
  }

  /** Same as `displayedWalls`, but mutates the caller's `out` set in
   *  place instead of allocating a fresh one. Used by main.ts's
   *  collision-wall rebuild, which runs every input tick (30 Hz) —
   *  avoiding the allocation keeps that path zero-garbage. */
  displayedWallsInto(serverWalls: Set<number>, out: Set<number>): void {
    out.clear();
    for (const k of serverWalls) out.add(k);
    for (const a of this.pending) {
      if (a.type === "place") {
        out.add(packCellKey(a.cellX, a.cellY));
      } else {
        for (const c of a.cells) {
          out.delete(packCellKey(c.x, c.y));
        }
      }
    }
  }

  /** Cells the renderer should DRAW in addition to the server's wall
   *  list. Returns [cellX, cellY] tuples in insertion order. */
  predictedAdditions(): Array<[number, number]> {
    const additions: Array<[number, number]> = [];
    const seen = new Set<number>();
    for (const a of this.pending) {
      if (a.type !== "place") continue;
      const key = packCellKey(a.cellX, a.cellY);
      if (seen.has(key)) continue;
      seen.add(key);
      additions.push([a.cellX, a.cellY]);
    }
    return additions;
  }

  /** Cells (packed keys) the renderer should HIDE from the server's
   *  wall list. */
  predictedRemovals(): Set<number> {
    const removals = new Set<number>();
    for (const a of this.pending) {
      if (a.type !== "break") continue;
      for (const c of a.cells) {
        removals.add(packCellKey(c.x, c.y));
      }
    }
    return removals;
  }

  /** Diagnostic for HUD — pending action count + tracked own-wall count. */
  debugInfo(): { pending: number; owned: number } {
    return { pending: this.pending.length, owned: this.ownedCells.size };
  }

  /** Apply server snapshot. Updates `ownedCells` with newly-acked own
   *  walls + freshly-destroyed ones, then drops every pending action
   *  whose tick has been acknowledged by the server. */
  reconcile(lastInputTick: number, serverWalls: Set<number>): void {
    // 1. Newly-acked PLACE actions: if the cell IS in the snapshot's
    //    wall set, the server confirmed our placement — record it as
    //    one of ours so future BREAK predictions can target it.
    for (const a of this.pending) {
      if (a.type !== "place") continue;
      if (a.tick > lastInputTick) continue;
      const key = packCellKey(a.cellX, a.cellY);
      if (serverWalls.has(key)) {
        this.ownedCells.add(key);
      }
    }

    // 2. Prune own walls that have vanished from the snapshot (we broke
    //    them, an enemy shot destroyed them, or we left their AOI). Done
    //    second so the freshly-added cells from step 1 aren't pruned by
    //    a stale "not in snapshot" check.
    for (const key of this.ownedCells) {
      if (!serverWalls.has(key)) {
        this.ownedCells.delete(key);
      }
    }

    // 3. Drop acknowledged pending actions. From this point the
    //    snapshot's wall set is authoritative for these cells.
    this.pending = this.pending.filter((a) => a.tick > lastInputTick);
  }

  /** Wipe all state. Used on disconnect — stale predictions from a
   *  previous session would otherwise paint phantom walls. */
  reset(): void {
    this.pending = [];
    this.ownedCells.clear();
  }
}
